import {
  AfterViewInit,
  Component,
  computed,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Optional,
  Output,
  QueryList,
  signal,
  ViewChildren
} from '@angular/core';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { PlotlyComponent, PlotlyService, PlotlySharedModule } from 'angular-plotly.js';
import * as _ from 'lodash';
import { firstValueFrom, lastValueFrom } from 'rxjs';

import { BooleanInput, coerceBooleanProperty } from '@angular/cdk/coercion';
import { AsyncPipe, NgClass, NgTemplateOutlet } from '@angular/common';
import { AbstractControl, AsyncValidatorFn, FormControl, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatOptionModule } from '@angular/material/core';
import { BehaviorSubject, Observable, of, OperatorFunction, ReplaySubject, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, switchMap, take } from 'rxjs/operators';
import { MaterialImportsModule } from 'src/app/material-imports.module';
import { isAnnotationSeries } from 'src/app/model/annotation-series';
import { ICalibrationFile } from 'src/app/model/calibration-file';
import { IDataSeries, ISeriesIdentification } from 'src/app/model/data-series';
import { isDeviceSeries } from 'src/app/model/device/device-series';
import { ISeriesGroupByThingie } from 'src/app/model/series-tree';
import { IUserSetting_ShakerControlProfile } from 'src/app/model/shaker-control/shaker-control-userdata-template-types';
import { isThingieSeries } from 'src/app/model/thingie-series';
import { IReassignable, ITask, IUserInput, IUserSetting } from 'src/app/model/thingie/task';
import { IUserTemplate } from 'src/app/model/thingie/templates';
import { IThingie } from 'src/app/model/thingie/thingie';
import { IUserData } from 'src/app/model/userdata';
import { ITaskOutputKey } from 'src/app/services/common/websocket-connection.service';
import { ThingieDataService } from 'src/app/services/data/thingie-data.service';
import { LogService } from 'src/app/services/loggers/logger.service';
import { ThingieWizardContextService } from 'src/app/services/thingie-wizard-context/thingie-wizard-context.service';
import { ThingieService } from 'src/app/services/thingie/thingie-service.service';
import { UserDataService } from 'src/app/services/user-data/user-data.service';
import { deepCopy } from 'src/app/utility/deep-copy';
import { createEmptyThingieTask } from 'src/app/utility/default-object-creator';
import { isEqualWithIgnoringUndefinedProperties } from 'src/app/utility/equality';
import { ExperimentCreationValidator, ITaskCondition } from 'src/app/utility/experiment-creation-validator';
import { loadPlotly } from 'src/app/utility/plotly';
import { SeriesNameHelper } from 'src/app/utility/series/series-name';
import { ThingieStatusHelper } from 'src/app/utility/thingie-status-helper';
import { SubSink } from 'subsink';
import { PureFunctionCallPipe } from '../../utility/pipes/pure-member-call/pure-member-call.pipe';
import {
  FeedingProfileConfigDialogComponent,
  IDialogArgs as IFeedingProfileArgs
} from '../configuration-input-feature-module/feeding-profile-config-dialog/feeding-profile-config-dialog.component';
import { LisProfileGeneratorComponent } from '../configuration-input-feature-module/lis-profile-generator/lis-profile-generator.component';
import { ShakerControlTaskConfigurationComponent } from '../configuration-input-feature-module/shaker-control/shaker-control-task-configuration/shaker-control-task-configuration.component';
import { UserSettingInputComponent } from '../configuration-input-feature-module/usersetting-input/usersetting-input.component';
import { DialogService } from '../dialogs/dialog/dialog.service';
import { LoadingOverlayComponent } from '../loading-overlay/loading-overlay.component';
import { TaskInputValueDiffInfo } from '../process-configuration/process-configuration.component';
import { SeriesSelectorComponent } from '../series-selector/series-selector.component';
import { SpinnerComponent } from '../spinner/spinner.component';

type DiffInfo = TaskInputValueDiffInfo[0];

interface IInputWrapper {

  /**
   * The key which identifies the input
   * in a task's input map
   */
  key: string;

  /**
   * The actual input (type 'UserSetting') to configure
   */
  input: IUserSetting | IUserInput | IReassignable;

  /**
   * Can the input be diplayed in the UI?
   */
  displayConditionsMet: boolean;
}

@Component({
  selector: 'app-task-configuration',
  templateUrl: './task-configuration.component.html',
  styleUrls: ['./task-configuration.component.scss'],
  imports: [NgTemplateOutlet, MaterialImportsModule, UserSettingInputComponent, NgClass, MatOptionModule, PlotlySharedModule, LoadingOverlayComponent, SpinnerComponent, FormsModule, ReactiveFormsModule, ShakerControlTaskConfigurationComponent, AsyncPipe, PureFunctionCallPipe,]
})
export class TaskConfigurationComponent implements OnInit, AfterViewInit, OnDestroy {
  @HostListener('window:resize')
  protected _onResize() {
    this.resizeEvent$.next();
  }

  @ViewChildren(UserSettingInputComponent)
  private _userSettingInputList!: QueryList<UserSettingInputComponent>;

  @ViewChildren(PlotlyComponent)
  private _plotlyComponents?: QueryList<PlotlyComponent>;

  /**
   * Contains information which input has different values
   * in different thingies.
   *
   * E.g. if this task's input 'cheese' & 'poopy woop'
   * would have each itself have different values between
   * different thingies (e.g. thingie1.cheese = 'Rote Hexe', thingie2.cheese = 'Geramont')
   * the structure would look like this
   *
   * ````
   * {
   *   cheese: true,
   *   'poopy woop': true
   * }
   * ````
   */
  @Input()
  public set diffInfo(info: DiffInfo | undefined) {
    this.inputValueDiff = info ?? {};
    this.lisProfileOverrideConfirmed = false;
  }

  @Input()
  public set task(val: ITask | undefined) {
    if (val === undefined) {
      return;
    }
    this._task = val;
    this.taskHasBeenStarted = ThingieStatusHelper.hasBeenStarted(val);
    this.inputsToConfigure = this.obtainConfigurableInputsFromTaskAndCheckForAdvancedInputs(val);
    this.checkInputGroupsDisplayConditions();
    this.initializeCustomProfileValueFromTask(val);
    this.triggerGraphRedraw();
    this.determineTaskTemplateType();
  }

  public get task(): ITask {
    return this._task;
  }

  @Input()
  public set contextThingie(newThingie: IThingie | null) {
    if (newThingie !== null) {
      this._contextThingie = newThingie;
      this.contextThingie$.next(newThingie);
    }
  }
  public get contextThingie() {
    return this._contextThingie;
  }
  private _contextThingie: IThingie | null = null;

  @Input()
  public processIdx?: number;

  @Input()
  public taskIdx?: number;

  @Input()
  public set readonly(value: BooleanInput) {
    this.readonly$.next(coerceBooleanProperty(value));
  }

  public get readonly(): boolean {
    return this.readonly$.getValue();
  }

  /**
   * @see {@link readonly}
   */
  protected readonly$ = new BehaviorSubject(false);


  @Output()
  public taskChange = new EventEmitter<void>();

  @Input()
  public highlightFirstReplicateInSeriesSelector = false;

  /**
   * Whether to display the actual replicate name or 'Replicates' of the first replicate
   * in the series selector, because it's a proxy for multiple replicates
   */
  @Input()
  public hintProxyNameToSeriesSelector = false;


  private userTemplateList = signal<IUserTemplate[] | undefined>(undefined);

  protected liquidTypeTemplateList = computed(() => this.userTemplateList()?.filter(
    template => template.template.payload.inputElementType === 'table'
  ));

  protected lisTemplateList = computed(() => this.userTemplateList()?.filter(
    template => template.template.payload.inputElementType === 'lisprofile-generator'
  ) ?? []);

  /**
   * This function needs to be kept in sync with ExperimentCreationValidator.validateTask
   */
  public get isValid(): boolean {
    const usersettingsValid = Array
      .from(this._userSettingInputList)
      .filter(usc => usc.input?.condition === undefined || this.checkCondition(usc.input.condition))
      .every(usc => usc.isValid);

    const reassignablesValid = Object.entries(this.task.inputs)
      .filter((entry): entry is [string, IReassignable] => {
        const [,input] = entry;
        return input.source === 'reassignable';
      })
      .filter(([,input]) => input.condition === undefined || this.checkCondition(input.condition))
      .filter(([key, input]) => input.optional !== true && !this._inputRequiresOverride(key))
      .every(([key, input]) => input.value !== undefined && this.reassignableSeriesFormControls[key].validator?.(this.reassignableSeriesFormControls[key]) === null);

    this.feedingProfileValid = Object.entries(this.task.inputs)
      .filter(([key, i]) => i.source === 'usersetting' && i.inputElementType === 'lisprofile-generator' && !this._inputRequiresOverride(key))
      .filter(([, i]) => i.source === 'usersetting' && (i.condition === undefined || this.checkCondition(i.condition)))
      .every(([,e]) => ((e as IUserSetting).value as []).length > 0);

    const shakingProfileValid = Object.entries(this.task.inputs)
      .filter(([key, i]) => i.source === 'usersetting' && i.inputElementType === 'shaker-control' && !this._inputRequiresOverride(key))
      .filter(([, i]) => i.source === 'usersetting' && (i.condition === undefined || this.checkCondition(i.condition)))
      .every(([,e]) => {
        const s = e as IUserSetting_ShakerControlProfile;
        if (s.templateId === undefined || s.value === undefined) {
          return false;
        }

        // Check that each selected feature has an initial value
        return s.value.features.every(feature => {
          if (s.value.initialValues.find(v => v.type === feature) === undefined) {
            return false;
          }
          return true;
        });
      });

    return usersettingsValid
      && reassignablesValid
      && this.feedingProfileValid
      && shakingProfileValid;
  }

  protected readonly defaultInputGroupName = '';

  protected advancedOptionsEnabled = false;
  protected traceForPlotly = {};
  protected layout = {
    autosize: true,
    margin: {
      l: 60,
      r: 0,
      b: 70,
      t: 60,
    },
    title: 'Preview',
    height: 300,
    xaxis: {
      title: {
        text: 'Time since profile start [hours]',
      },
      automargin: true,
    },
    yaxis: {
      title: {
        text: 'Volume [ml]',
      },
      automargin: true,
    }
  };

  protected lisProfileOverrideConfirmed = false;
  protected pHCalibrationList?: IUserTemplate[] = [];
  protected DOCalibrationList?: IUserTemplate[] = [];

  protected get hasAdvancedInputs(): boolean {
    return (this.inputsToConfigure?.advancedGroups.length ?? -1) > 0;
  }

  // This is the map of inputs to configure
  protected inputsToConfigure?: {
    basicGroups: { groupName: string | undefined; groupInputs: IInputWrapper[] }[];
    advancedGroups: { groupName: string | undefined; groupInputs: IInputWrapper[] }[];

    displayBasicGroups: boolean;
    displayAdvancedGroups: boolean;
  };
  protected inputValueDiff: DiffInfo = {};

  protected taskTypeTooltip = 'Base template for this task';
  protected loadingData = false;
  protected selectedFeedingProfile = UserDataService.customTemplateIndicator;
  protected feedingProfileValid = true;

  protected taskHasBeenStarted = false;

  private redraw$ = new BehaviorSubject<number>(0);

  protected reassignableSeriesFormControls: { [key: string]: FormControl } = {};

  // Used as value for one <mat-option/> within the HTML template
  protected readonly customFeedingProfileName = UserDataService.customTemplateIndicator;

  protected readonly plotlyConfig = {
    displaylogo: false,
    displayModeBar: true
  };

  private customFeedingProfileValue: unknown = undefined;
  private resizeEvent$ = new Subject<void>();
  private subscriptions = new SubSink();
  private _task: ITask = createEmptyThingieTask();
  protected contextThingie$ = new ReplaySubject<IThingie>(1);

  protected readonly overrideHintText = 'Changes will overwrite replicate configurations.';

  constructor(
    @Optional() private contextService: ThingieWizardContextService | null,
    private log: LogService,
    private plotlyService: PlotlyService,
    private dialogService: DialogService,
    private thingieDataService: ThingieDataService,
    private thingieService: ThingieService,
    private userData: UserDataService,
  ) {
    this._inputRequiresOverride = this._inputRequiresOverride.bind(this);

    void loadPlotly();

    this.traceForPlotly = {
      type: 'scatter',
      x: [],
      y: [],
      mode: 'lines',
      name: 'Volume',
      line: {
        color: 'rgb(219, 64, 82)',
        width: 3
      },
    };
  }

  public async ngOnInit() {
    this.subscriptions.sink = this.readonly$.pipe(
      switchMap(
        readonly => readonly
          ? this.userData.getAllTemplates({ disabled: true, fromPreviousVersion: true })
          : this.userData.getAllTemplates()
      )
    ).subscribe(next => this.userTemplateList.set(next));

    await this.loadTemplateLists();
    this.determineTaskTemplateType();

    this.subscriptions.sink = this.redraw$.pipe(
      distinctUntilChanged()
    ).subscribe(() => void this.updatePlotly());

    this.subscriptions.sink = this.contextService?.configChange$.subscribe(() => {
      const validityChanged = Object.values(this.reassignableSeriesFormControls)
        .map(control => {
          const previouslyValid = control.valid;
          control.updateValueAndValidity();
          return previouslyValid !== control.valid;
        })
        .some(x => x);
      if (validityChanged) {
        this.taskChange.next();
      }
    });
  }

  public ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  public ngAfterViewInit(): void {
    this.subscriptions.sink = this.resizeEvent$.pipe(
      debounceTime(16)
    ).subscribe(this.handleResizeEvent.bind(this));

    // last step would be check the conditions and apply if are needed
    this._checkAndApplyCondition();
  }

  protected _confirmLisProfileOverride(inputKey: string) {
    this.lisProfileOverrideConfirmed = true;

    (this._task!.inputs[inputKey] as IUserSetting).value
      = this.inputValueDiff[inputKey].value;

    const templateId = this.inputValueDiff[inputKey].templateId;
    (this._task!.inputs[inputKey] as IUserSetting).templateId = templateId;
    if (templateId !== undefined) {
      this.onFeedingProfileChange(templateId, inputKey, false);
    }

    this.emitTaskUpdate();
  }

  protected async openSeriesSelector(wrapper: IInputWrapper, removeSeriesFromCurrentTask: boolean = false) {
    const thingie = (await lastValueFrom(this.contextThingie$.pipe(take(1))))!;
    const selectedSeriesInfo: ISeriesGroupByThingie[] = [];
    if (wrapper.input.value) {
      const series = await this.getSeriesSelectionFromTaskKey(wrapper, this.contextThingie$);
      const value = wrapper.input.value as ITaskOutputKey;
      if (series !== undefined) {
        selectedSeriesInfo.push({
          id: value.task.thingie as string,
          info: {
            seriesInfoList: [
              {
                name: '',
                series
              }
            ]
          }
        });
      }
    }
    let seriesFilter: ((seriesIdentification: ISeriesIdentification) => boolean) | undefined;
    if (removeSeriesFromCurrentTask) {
      seriesFilter = (id) => id.type !== 'thingieSeries' || !(id.thingieId === this.contextThingie?._id && id.processIndex === this.processIdx && id.taskIndex === this.taskIdx);
    }

    let primaryThingieInSelector = thingie;
    if (this.hintProxyNameToSeriesSelector) {
      primaryThingieInSelector = await deepCopy(thingie);
      primaryThingieInSelector.name = 'Replicates';
    }

    await this.dialogService.open({
      component: SeriesSelectorComponent,
      data: {
        primaryThingie: primaryThingieInSelector,
        thingieGroup: selectedSeriesInfo,
        isDialog: true,
        multiSeriesSelection: false,
        seriesFilter,
        highlightFirstReplicate: this.highlightFirstReplicateInSeriesSelector
      }
    })
      .afterClosed()
      .then((data) => {
        if (data) {
          void this._onSeriesSelection(wrapper, data.selectedSeries);
        } else {
          this.reassignableSeriesFormControls[wrapper.key].markAllAsTouched(); // trigger validation
        }
      })
      .catch(err => this.log.error('Error selecting series', err));
  }

  protected onSeriesValueClick(event: MouseEvent, wrapper: IInputWrapper, disabled: boolean) {
    if (disabled || this.hasSelectedTextInElement(event.currentTarget)) {
      return;
    }

    void this.openSeriesSelector(wrapper, true);
  }

  protected _openLisGenerator(wrapper: IInputWrapper, readonly: boolean) {
    const templateName = this
      .lisTemplateList()
      ?.find(t => t._id === this.selectedFeedingProfile)?.template.name
      ?? UserDataService.customTemplateIndicator;

    const dialogArgs: IFeedingProfileArgs = {
      inputKey: wrapper.key,
      task: this._task,
      templateName,
      readonly
    };

    void this
      .dialogService
      .open({ component: FeedingProfileConfigDialogComponent, data: dialogArgs })
      .afterClosed()
      .then(result => {
        if (readonly === true) {
          return;
        }
        if (typeof result === 'object') {
          (this._task.inputs[wrapper.key] as IUserSetting).value = result.profile;

          let action: Promise<void>;

          if (result.profileId) {
            if (result.profileId !== this.customFeedingProfileName) {
              action = this.loadTemplateLists().then(() => this.reloadUserTemplates());
            } else {  // Custom feeding profile
              this.customFeedingProfileValue = result.profile;
              action = Promise.resolve();
            }

            void action.then(() => this.onFeedingProfileChange(result.profileId!, wrapper.key));
          }
        }
      });
  }

  private _checkAndApplyCondition() {
    for (const key of Object.keys(this._task!.inputs)) {
      const input = this._task!.inputs[key];
      if (input.source === 'usersetting' && input.condition !== undefined) {
        Object.keys(input.condition)
          .filter(key => key.startsWith('! disableIf'))
          .forEach(key => {
            const match = key.match(/! disableIf\((\w+)\)/);
            if (match === null) {
              return;
            }
            const matchingKey = match[1];
            const matchingInput = this._task.inputs[matchingKey] as IUserSetting;

            const disabled = !matchingInput.value;
            if (disabled) {
              input.uneditable = true;
            } else {
              delete input.uneditable;
            }
          });
      }
    }
  }

  protected _onInputUpdated(wrapper: IInputWrapper) {
    this._checkAndApplyCondition();
    this.doDisplayConditionCheck();
    this.checkInputGroupsDisplayConditions();
    this.applyOnChangeOverwriteCondition(wrapper.input);

    this.emitTaskUpdate();
  }

  protected onFeedingProfileChange(profileId: string, inputKey: string, emitUpdate = true) {
    this.selectedFeedingProfile = profileId;
    const input = (this._task!.inputs[inputKey] as IUserSetting);

    if (profileId !== this.customFeedingProfileName) {
      const templateWrapper = this.lisTemplateList()?.find(
        p => p._id === profileId
      )!;

      input.templateId = templateWrapper._id;
      input.value = templateWrapper.template.payload.value;
    } else {  // Custom feeding profile
      input.value = this.customFeedingProfileValue;
      input.templateId = undefined;
    }


    void this.updatePlotly();
    if (emitUpdate) {
      this.emitTaskUpdate();
    }
  }

  protected _inputRequiresOverride(key: string): boolean {
    return this.inputValueDiff[key] !== undefined;
  }

  private hasSelectedTextInElement(target: EventTarget | null): boolean {
    if (!(target instanceof Node)) {
      return false;
    }

    const selection = document.getSelection();
    if (selection === null || selection.isCollapsed || selection.rangeCount === 0) {
      return false;
    }

    return Array.from({ length: selection.rangeCount })
      .some((_, index) => selection.getRangeAt(index).intersectsNode(target));
  }

  private async updatePlotly() {
    if (this._task === undefined) {
      return;
    }

    const lisInput = Object.values(this._task.inputs)
      .find((input): input is IUserSetting => input.source === 'usersetting' && input.inputElementType === 'lisprofile-generator');
    if (lisInput === undefined || lisInput.value === undefined) {
      // task has no lis profile inputs, so no plot needed
      return;
    }

    this.loadingData = true; // start loading spinner
    const { trace, unit } = await LisProfileGeneratorComponent.getLisProfileDataTrace(
      this.thingieDataService,
      this._task
    );
    this.traceForPlotly = trace;
    this.layout.xaxis.title.text = `Time since profile start [${unit}]`;
    this.loadingData = false;
  }

  private async loadTemplateLists() {
    const userDataToTemplateMap: OperatorFunction<IUserData<ICalibrationFile>[], IUserTemplate[]>
      = map(elements => elements.map(element => {
        const res: IUserTemplate = {
          _id: element._id!,
          type: element.type,
          version: 'userSettingTemplate',
          created: element.createdAt ?? '',
          template: {
            name: element.value.batchName,
            type: element.type,
            payload: {
              source: 'usersetting',
              item: {
                structureInfo: [],
                valueInfo: [],
                visibility: 'basic'
              },
              value: element.value
            }
          }
        };
        return res;
      }));
    this.pHCalibrationList = (await lastValueFrom(this.userData.getAllUserData('PH_CALIBRATION')
      .pipe(userDataToTemplateMap)))!;
    this.DOCalibrationList = (await lastValueFrom(this.userData.getAllUserData('DO_CALIBRATION')
      .pipe(userDataToTemplateMap)))!;
  }

  private async reloadUserTemplates(): Promise<void> {
    const readonly = this.readonly$.getValue();
    const templates = await (readonly
      ? this.userData.getAllTemplates({ disabled: true, fromPreviousVersion: true })
      : this.userData.getAllTemplates());
    this.userTemplateList.set(templates);
  }

  private obtainConfigurableInputsFromTaskAndCheckForAdvancedInputs(task: ITask): TaskConfigurationComponent['inputsToConfigure'] {
    // Get inputs to show in the wizard
    const inputs = Object.entries(task.inputs)
      .filter((entry): entry is [string, IUserSetting | IReassignable | IUserInput] => {
        const input = entry[1];
        return input.source === 'usersetting' || input.source === 'reassignable' || input.source === 'userinput';
      })
      .map<IInputWrapper>(([key, input]) => {
        let displayConditionsMet = this.checkInputDisplayConditions(input);

        return { key, input, displayConditionsMet };
      })
      // Don't show optional userinput inputs in the wizard
      .filter(({ input }) => !(input.source === 'userinput' && (input as IUserInput).optional === true));

    // Do some processing of the inputs
    inputs.forEach((wrapper) => {
      const { key, input } = wrapper;

      if (input.inputElementType === 'lisprofile-generator' && input.templateId) {
        this.selectedFeedingProfile = input.templateId;
      }

      if (input.source === 'reassignable') {
        this.reassignableSeriesFormControls[key] = new FormControl('', {
          validators: [Validators.required],
          asyncValidators: [this.seriesNotDisabledValidator(key)]
        });
        if (input.value !== undefined) {
          void this._onSeriesSelection(wrapper);
        }
      }
    });

    const basicInputs = inputs.filter(({ input }) => input.item.visibility === 'basic');
    const advancedInputs = inputs.filter(({ input }) => input.item.visibility === 'advanced');

    // group by input group
    const basicGroups = _.chain(basicInputs).groupBy((wrapper) => wrapper.input.group ?? '')
      .sortBy(group => basicInputs.indexOf(group[0]))
      .map(inputs => ({ groupName: inputs[0].input.group?.replace(/__.*$/, ''), groupInputs: inputs }))
      .value();
    const advancedGroups = _.chain(advancedInputs).groupBy((wrapper) => wrapper.input.group ?? '')
      .sortBy(group => advancedInputs.indexOf(group[0]))
      .map(inputs => ({ groupName: inputs[0].input.group?.replace(/__.*$/, ''), groupInputs: inputs }))
      .value();

    return {
      basicGroups,
      advancedGroups,
      displayBasicGroups: true,
      displayAdvancedGroups: false
    };
  }

  /**
   * Check all input's display conditions and update the displayConditionsMet property
   * of each input.
   */
  private doDisplayConditionCheck() {
    if (!this.inputsToConfigure) {
      return;
    }

    // Basic inputs
    for (const group of this.inputsToConfigure.basicGroups) {
      for (const input of group.groupInputs) {
        input.displayConditionsMet = this.checkInputDisplayConditions(input.input);
      }
    }

    // Advanced inputs
    for (const group of this.inputsToConfigure.advancedGroups) {
      for (const input of group.groupInputs) {
        input.displayConditionsMet = this.checkInputDisplayConditions(input.input);
      }
    }
  }


  /**
   * Determines if input groups are to be shown or not, based on the display-
   * conditions of the underlying inputs.
   */
  private checkInputGroupsDisplayConditions() {
    if (!this.inputsToConfigure) {
      return;
    }

    this.inputsToConfigure.displayBasicGroups = this.inputsToConfigure.basicGroups.some(group =>
      group.groupInputs.some(input => input.displayConditionsMet));

    this.inputsToConfigure.displayAdvancedGroups = this.inputsToConfigure.advancedGroups.some(group =>
      group.groupInputs.some(input => input.displayConditionsMet));
  }

  /**
   * Helper method to check if an input should be displayed in the UI
   *
   * Note: if an input does not have conditions to fulfill, its display conditions are met.
   */
  private checkInputDisplayConditions(input: IUserSetting | IUserInput | IReassignable) {
    let displayConditionsMet = false;

    if (input.source === 'usersetting' || input.source === 'reassignable') {
      if (input.condition === undefined) {
        displayConditionsMet = true;
      } else {
        displayConditionsMet = this.checkCondition(input.condition);
      }
    } else {
      displayConditionsMet = true;
    }

    return displayConditionsMet;
  }

  private async getSeriesSelectionFromTaskKey(wrapper: IInputWrapper, contextThingie: Observable<IThingie>) {
    const value = wrapper.input.value as ITaskOutputKey;
    const contextThingieValue = (await lastValueFrom(contextThingie.pipe(take(1))))!;
    let thingie = contextThingieValue;
    if (value.task.thingie !== undefined) {
      thingie = (await lastValueFrom(this.thingieService.getThingieById(value.task.thingie)
        .pipe(take(1))))!;
    }

    let series;
    if (value.task.thingie === undefined
      && value.task.processIdx === undefined
      && value.task.taskIdx === undefined
    ) {
      //When the reference to the thingie, process and task is unknown then we need to search by name for the correct series.
      //  Can happen when setting a reassignable input value to a series withing the same task
      series = this.thingieService.getAvailableDataSeriesFromThingie(thingie, false).find(series =>
        series.identification.type === 'thingieSeries'
        && series.identification.output === value.name);
    } else {
      series = this.thingieService.getAvailableDataSeriesFromThingie(thingie, false).find(series =>
        series.identification.type === 'thingieSeries'
        && (series.identification.thingieId === value.task.thingie ||
          (value.task.thingie === undefined && contextThingieValue._id === series.identification.thingieId))
        && series.identification.processIndex === value.task.processIdx
        && series.identification.taskIndex === value.task.taskIdx
        && series.identification.output === value.name);
    }
    if (series === undefined) {
      // series does not exist on this thingie, so the user has to select a different one.
      // unset the input value
      wrapper.input.value = undefined;
    }
    if (series !== undefined && value.task.thingie === undefined) {
      // for consistency, series identification needs to be undefined if the series comes from the context thingie
      series.identification.thingieId = undefined;
    }
    return series;
  }

  private seriesNotDisabledValidator(key: string): AsyncValidatorFn {
    return async (control: AbstractControl) => {
      const valid = await ExperimentCreationValidator.validateReassignableSeriesNotDisabled(this.task.inputs[key], this.contextThingie, this.thingieService);
      if (!valid) {
        // this is an important error, always show it
        control.markAsTouched();

        return {
          seriesDisabled: 'The selected series is no longer available.'
        };
      }
      return null;
    };
  }

  /**
   * Helper method to emit a copy of the task
   * associated with this instance
   */
  private emitTaskUpdate() {
    this.taskChange.emit();
  }

  private handleResizeEvent() {
    this._plotlyComponents?.forEach(
      c => {
        const rect = (c.plotEl.nativeElement as HTMLElement).getBoundingClientRect();
        if (rect.height && rect.width) {
          this.plotlyService.resize(c.plotEl.nativeElement);
        }
      }
    );
  }

  private initializeCustomProfileValueFromTask(val: ITask) {
    // Reset the custom feeding profile to some arbitrary default
    // doing no dispensing (0 microliters / 1 minute).
    this.customFeedingProfileValue = [['Constant', 60, 0, 1, 2]];

    for (const key of Object.keys(val.inputs)) {
      const input = val.inputs[key];

      if (
        input.source === 'usersetting'
        && input.inputElementType === 'lisprofile-generator'
        && input.name === this.customFeedingProfileName
      ) {
        this.customFeedingProfileValue = input.value;
        return;
      }
    }
  }

  protected advancedOptionsToggled($event: MatSlideToggleChange) {
    this.advancedOptionsEnabled = $event.checked;
  }

  private triggerGraphRedraw() {
    this.redraw$.next(this.redraw$.value + 1);
  }

  protected checkCondition(condition: ITaskCondition) {
    return ExperimentCreationValidator.validateTaskInputCondition(condition, this.task);
  }

  protected async _onSeriesSelection(wrapper: IInputWrapper, seriesEvent?: IDataSeries[]) {
    let series;
    if (seriesEvent === undefined) {
      series = await this.getSeriesSelectionFromTaskKey(wrapper, this.contextThingie$);
    } else {
      series = seriesEvent[seriesEvent.length - 1];
    }

    if (!wrapper.input) {
      throw new Error('input not set');
    }
    if (series === undefined) {
      wrapper.input.value = undefined;
      this.reassignableSeriesFormControls[wrapper.key].setValue(wrapper.input.name ?? null);
    } else {
      if (!isThingieSeries(series)) {
        throw new Error('Need to select a thingie series');
      }

      const reference: ITaskOutputKey = {
        keyType: 'taskOutput',
        name: series.identification.output,
        task: {
          thingie: series.identification.thingieId,
          processIdx: series.identification.processIndex,
          taskIdx: series.identification.taskIndex
        }
      };

      // to have unique structure of valid data that can be used in mat-select
      // if we do not delete the undefined data,
      // then this object won't find it's match after getting updated in UI
      if (reference.task.thingie === undefined) {
        delete reference.task.thingie;
      }

      wrapper.input.value = reference;

      const seriesNameHelper = new SeriesNameHelper({ thingieService: this.thingieService, getDeviceByAddress: () => of(undefined) });
      const seriesName = (await firstValueFrom(seriesNameHelper.getSeriesLongName(series, this.contextThingie$)))!;
      this.reassignableSeriesFormControls[wrapper.key].setValue(seriesName);
    }

    // apply a unitMatches condition if necessary.
    // This modifies the specified input and sets its unit to be the unit of
    // the selected series.
    const unit = series?.dataItemDescription.value?.valueInfo?.[0]?.unit;
    if (wrapper.input.source === 'reassignable') {
      if (wrapper.input.condition !== undefined) {
        Object.keys(wrapper.input.condition)
          .filter(key => key.startsWith('! unitMatches'))
          .forEach(key => {
            const match = key.match(/! unitMatches\((\w+)\)/);
            if (match === null) {
              return;
            }
            const matchingKey = match[1];
            const matchingInput = this.task.inputs[matchingKey];
            if (matchingInput === null || (matchingInput.source !== 'usersetting' && matchingInput.source !== 'userinput')) {
              return;
            }
            matchingInput.item.valueInfo[0].unit = unit ?? '';
          });
      }
    }
    this.applyOnChangeOverwriteCondition(wrapper.input);
    this.emitTaskUpdate();
  }

  /**
   * Applies the onChangeOverwrite condition to the input.
   * This is used to apply a condition that is set in the task template
   * to the input.
   */
  private applyOnChangeOverwriteCondition(input: IUserSetting | IUserInput | IReassignable) {
    if ((input.source === 'reassignable' || input.source === 'usersetting') && input.condition !== undefined) {
      Object.keys(input.condition)
        .filter(key => key.startsWith('! onChangeOverwrite'))
        .forEach(key => {
          const match = key.match(/! onChangeOverwrite\((\w+)\.([^)]+)\)/);
          if (match === null) {
            return;
          }
          const matchingKey = match[1];
          const matchingInput = this.task.inputs[matchingKey];
          const matchingProperty = match[2];
          const matchingPropertyParts = matchingProperty.split('.');
          if (matchingInput === null || (matchingInput.source !== 'usersetting' && matchingInput.source !== 'userinput' && matchingInput.source !== 'dynamicinput')) {
            return;
          }
          let obj: any = matchingInput;
          for (let i = 0; i < matchingPropertyParts.length - 1; i++) {
            obj = obj[matchingPropertyParts[i]];
          }
          let value = input.value as string | number;
          if (input.inputElementType === 'select' && _.isArray(input.item.valueInfo[0].options?.[0])) {
            value = input.item.valueInfo[0].options?.findIndex(opt =>
              isEqualWithIgnoringUndefinedProperties(input.value as object, opt[0])) ?? value;
          }

          const propKey = matchingPropertyParts[matchingPropertyParts.length - 1];
          const newValue = input.condition[key][value];
          if (isEqualWithIgnoringUndefinedProperties(obj[propKey], newValue)) {
            // since re-assigning `task` re-triggers its setter, which (for reassignable
            // inputs that already have a value) re-runs this same method - an
            // infinite loop if the computed value never changes.
            return;
          }
          obj[propKey] = newValue;

          // force to update the UI with new values
          this.task = { ...this.task };

          if (matchingInput.source === 'usersetting' && matchingInput.condition?.['! disableSubfields'] && _.isArray(matchingInput.value)) {
            // maybe now some checked subfields are disabled. Uncheck them.
            for (let i = 0; i < (matchingInput.item.structureInfo[0].options?.length ?? 0); i++) {
              for (let j = 0; j < (matchingInput.item.structureInfo[1].options?.length ?? 0); j++) {
                if (matchingInput.value[i]?.[j] === true && matchingInput.condition['! disableSubfields'][i]?.[j] === true) {
                  matchingInput.value[i][j] = false;
                }
              }
            }
          }
        });
    }
  }

  private async getSeriesDisplayName(series: IDataSeries, contextThingie: Observable<IThingie>) {
    let displayName = '';
    if (isDeviceSeries(series)) {
      displayName = series.dataItemDescription[series.identification.property]
        .valueInfo[0]
        .name;
    } else if (isThingieSeries(series)) {
      let thingie: IThingie;
      if (series.identification.thingieId === undefined) {
        thingie = (await lastValueFrom(contextThingie.pipe(take(1))))!;
      } else {
        thingie = (await lastValueFrom(this.thingieService.getThingieById(series.identification.thingieId)
          .pipe(take(1))))!;
      }
      const processId = series.identification.processIndex;
      const taskId = series.identification.taskIndex;
      const isContextThingie = series.identification.thingieId === (await lastValueFrom(this.contextThingie$.pipe(take(1), map(t => t._id))))!;
      displayName =
      `${!isContextThingie ? thingie.name + ' / ' : ''}${thingie.processes[processId].name} / ${thingie.processes[processId].tasks[taskId].name} / ${series.description}`;
    } else if (isAnnotationSeries(series)) {
      displayName = series.identification.systemAnnotation ? 'System annotations' : 'User annotations';
    } else {
      displayName = 'unknown series';
    }

    return displayName;
  }

  private determineTaskTemplateType() {
    // Try to check if the current task is based on smart feeding.
    // If the check cannot be made, this we can continue without an error
    // because the result is used to update an axis title. (bad ux; functionality not affected)

    if (this.task.createdFromTemplateId === undefined) {
      this.log.warn('Template ID for task is not specified.');
      return;
    }
  }

  protected getLisTemplateNameById(args: { id: string; templates: IUserTemplate[] | undefined }): string {
    const { id, templates } = args;
    if (templates === undefined) {
      return '<unknown>';
    }
    if (id === undefined) {
      return 'Custom profile';
    }
    const template =  templates.find(t => t._id === id);
    if (template === undefined) {
      return '<unknown>';
    }
    return template.template.name;
  }


  protected _onShakerProfileUpdate() {
    this.emitTaskUpdate();
  }
}

